import React, { useState, useEffect } from "react";
import { FaFacebook, FaInstagram, FaPhone, FaPhoneAlt } from "react-icons/fa";
import { HiMenuAlt3, HiX } from "react-icons/hi";
import { Link } from "react-scroll";

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  const navLinks = [
    { href: "home", label: "Strona główna" },
    { href: "aboutus", label: "O nas" },
    { href: "menu", label: "Menu" },
    { href: "gallery", label: "Galeria" },
    { href: "contact-us", label: "Kontakt" },
  ];

  const socialLinks = [
    {
      href: "https://www.facebook.com/AnatomiaSmaku",
      icon: FaFacebook,
      label: "Facebook",
      hoverColor: "hover:text-blue-600",
    },
    {
      href: "https://www.instagram.com/anatomia.smaku/",
      icon: FaInstagram,
      label: "Instagram",
      hoverColor: "hover:text-pink-600",
    },
  ];

  return (
    <>
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-cream-100/95 backdrop-blur-md shadow-lg py-2"
            : "bg-cream-100 py-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link
              to="home"
              smooth={true}
              duration={500}
              className="cursor-pointer flex items-center group"
              aria-label="Anatomia Smaku - Strona główna"
            >
              <img
                src="/images/anatomia_logo.png"
                className={`transition-all duration-300 ${
                  isScrolled ? "h-16" : "h-20"
                } w-auto group-hover:scale-105`}
                alt="Anatomia Smaku Logo"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  spy={true}
                  smooth={true}
                  duration={500}
                  offset={-80}
                  onSetActive={() => setActiveLink(item.href)}
                  className={`cursor-pointer px-4 py-2 rounded-full text-charcoal-700 font-medium transition-all duration-300 relative group ${
                    activeLink === item.href
                      ? "text-cream-700"
                      : "hover:text-cream-700"
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 bg-cream-600 transform origin-left transition-transform duration-300 ${
                      activeLink === item.href
                        ? "scale-x-100"
                        : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  />
                </Link>
              ))}
            </div>

            {/* Right Side - Desktop */}
            <div className="hidden lg:flex items-center gap-6">
              {/* Phone Number */}
              <a
                href="tel:504784337"
                className="flex items-center gap-2 text-charcoal-700 hover:text-cream-700 transition-colors duration-300"
              >
                <FaPhoneAlt className="w-4 h-4" />
                <span className="font-medium">504 784 337</span>
              </a>

              {/* Divider */}
              <div className="h-6 w-px bg-cream-300" />

              {/* Social Icons */}
              <div className="flex items-center gap-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-charcoal-600 ${social.hoverColor} transition-all duration-300 transform hover:scale-110`}
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>

              {/* CTA Button */}
              <Link
                to="contact-us"
                smooth={true}
                duration={500}
                offset={-80}
                className="cursor-pointer"
              >
                <button className="bg-cream-600 hover:bg-cream-700 text-white font-medium py-2.5 px-6 rounded-full transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg">
                  Zamów teraz
                </button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden relative w-10 h-10 rounded-lg bg-cream-200 hover:bg-cream-300 transition-colors duration-300 flex items-center justify-center group"
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
            >
              <span className="sr-only">Menu</span>
              <div className="relative w-6 h-6">
                <HiMenuAlt3
                  className={`absolute inset-0 w-6 h-6 text-charcoal-700 transition-all duration-300 ${
                    isMenuOpen ? "opacity-0 rotate-180" : "opacity-100 rotate-0"
                  }`}
                />
                <HiX
                  className={`absolute inset-0 w-6 h-6 text-charcoal-700 transition-all duration-300 ${
                    isMenuOpen
                      ? "opacity-100 rotate-0"
                      : "opacity-0 -rotate-180"
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {/* Mobile Menu - Wariant 1: Fullscreen Creative */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-cream-100">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-cream-200 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cream-300 rounded-full blur-3xl opacity-30 translate-y-1/2 -translate-x-1/2" />

          <div className="relative h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6">
              <img
                src="/images/anatomia_logo.png"
                className="h-12 w-auto"
                alt="Anatomia Smaku Logo"
              />
              <button
                onClick={() => setIsMenuOpen(false)}
                className="w-12 h-12 rounded-full bg-cream-200 hover:bg-cream-300 transition-all duration-300 flex items-center justify-center group"
                aria-label="Close menu"
              >
                <HiX className="w-6 h-6 text-charcoal-700 group-hover:rotate-90 transition-transform duration-300" />
              </button>
            </div>

            {/* Navigation Links - Centered */}
            <nav className="flex-1 flex items-center justify-center px-8">
              <ul className="space-y-2 w-full max-w-sm">
                {navLinks.map((item, index) => (
                  <li
                    key={item.label}
                    style={{ animationDelay: `${index * 100}ms` }}
                    className={`${isMenuOpen ? "animate-fadeInUp" : ""}`}
                  >
                    <Link
                      to={item.href}
                      spy={true}
                      smooth={true}
                      duration={500}
                      offset={-80}
                      onClick={() => {
                        setIsMenuOpen(false);
                        setActiveLink(item.href);
                      }}
                      className={`block py-4 text-center text-2xl font-bold transition-all duration-300 relative overflow-hidden group  ${
                        activeLink === item.href
                          ? "text-cream-700"
                          : "text-charcoal-700"
                      }`}
                    >
                      <span className="relative z-10">{item.label}</span>
                      <div
                        className={`absolute inset-0 bg-cream-200 transform transition-transform duration-300 origin-left rounded-lg
                          ${
                            activeLink === item.href
                              ? "scale-x-100"
                              : "scale-x-0"
                          }`}
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Footer */}
            <div className="p-8 space-y-6">
              {/* Contact Info */}
              <div className="text-center space-y-2 flex flex-col items-center">
                <p className="text-sm text-charcoal-600">Zadzwoń do nas</p>
                <a
                  href="tel:504784337"
                  className="text-2xl font-bold text-charcoal-800 hover:text-cream-700 transition-colors"
                >
                  504 784 337
                </a>
                <a
                  href="tel:504784337"
                  className="text-2xl font-bold text-charcoal-800 hover:text-cream-700 transition-colors"
                >
                  504 769 855
                </a>
              </div>

              {/* Social Links */}
              <div className="flex justify-center gap-6">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-charcoal-600 ${social.hoverColor} transition-all duration-300 transform hover:scale-125`}
                    aria-label={social.label}
                  >
                    <social.icon className="w-8 h-8" />
                  </a>
                ))}
              </div>

              {/* CTA Button */}
              <Link
                to="contact-us"
                smooth={true}
                duration={500}
                offset={-80}
                onClick={() => setIsMenuOpen(false)}
                className="block"
              >
                <button className="w-full bg-charcoal-800 hover:bg-charcoal-900 text-cream-100 font-bold py-4 px-6 rounded-full transition-all duration-300 transform hover:scale-105">
                  Zamów catering
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
